import styled from 'styled-components';
import { H4, P, TypographyProps } from '@components/Typography';

/* Outer wrapper */
export const Root = styled.section<{ isLastItem?: boolean }>`
  background: ${({ theme }) => theme.color.white};
  padding: 7.5rem 0;
  padding-bottom: ${({ isLastItem }) => (isLastItem ? '0' : '7.5rem')};

  @media (min-width: ${({ theme }) => theme.breakpoint.width1080}) {
    .mobile-first-image {
      display: none;
    }
  }
  @media (max-width: ${({ theme }) => theme.breakpoint.width1080}) {
    padding: 4rem 0;

    .mobile-first-image {
      display: block;
      margin-bottom: 2rem;
      margin-right: 2rem;
    }
  }
`;

/* This defines the HEIGHT (content only) */
export const Row = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;

  @media (max-width: ${({ theme }) => theme.breakpoint.width1080}) {
    display: flex;
    flex-direction: column;
  }
`;

/* CONTENT DEFINES HEIGHT */
export const ContentSide = styled.div<{
  $orientation: 'left' | 'right';
  verticalContentPadding?: string;
}>`
  grid-column: ${({ $orientation }) =>
    $orientation === 'left' ? '2 / 3' : '1 / 2'};

  padding: ${({ verticalContentPadding }) =>
    verticalContentPadding
      ? `${verticalContentPadding} 25% ${verticalContentPadding} 19%`
      : '0rem 25% 0rem 19%'};
  z-index: 1;
  min-height: 50rem;

  @media (min-width: ${({ theme }) => theme.breakpoint.width1080}) {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.width1280}) {
    padding: ${({ verticalContentPadding }) =>
      verticalContentPadding
        ? `${verticalContentPadding} 18% ${verticalContentPadding} 12%`
        : '0rem 18% 0rem 12%'};
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.width1080}) {
    grid-column: auto;
    padding: ${({ verticalContentPadding }) =>
      verticalContentPadding ? `${verticalContentPadding} 2rem` : '0rem 2rem'};
    min-height: 0;
  }
`;

/* IMAGE IS ABSOLUTE → DOES NOT AFFECT HEIGHT */
export const ImageSide = styled.div<{
  $orientation: 'left' | 'right';
  $isLastItem?: boolean;
}>`
  box-sizing: border-box;
  position: absolute;
  top: 0;
  bottom: 0;

  ${({ $orientation }) =>
    $orientation === 'left'
      ? `
        left: 0;
        padding-right: 2rem;
      `
      : `
        right: 0;
        padding-left: 2rem;
      `}

  width: 50%;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoint.width1080}) {
    position: relative;
    width: 100%;
    padding: 0 2rem;
    border-radius: 0;
  }
`;

export const Images = styled.div<{ $isGrid2x2?: boolean }>`
  height: 100%;
  min-height: 100%;

  ${({ $isGrid2x2 }) =>
    $isGrid2x2
      ? `
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 1fr 1fr;
        gap: 1rem;
      `
      : `
        display: flex;
        flex-direction: column;
        gap: 1rem;
      `}
`;

export const ImageItem = styled.div<{
  borderTopLeftRadius?: string;
  borderTopRightRadius?: string;
  borderBottomLeftRadius?: string;
  borderBottomRightRadius?: string;
}>`
  flex: 1;
  overflow: hidden;

  border-top-left-radius: ${({ borderTopLeftRadius }) =>
    borderTopLeftRadius ?? '0'};
  border-top-right-radius: ${({ borderTopRightRadius }) =>
    borderTopRightRadius ?? '0'};
  border-bottom-left-radius: ${({ borderBottomLeftRadius }) =>
    borderBottomLeftRadius ?? '0'};
  border-bottom-right-radius: ${({ borderBottomRightRadius }) =>
    borderBottomRightRadius ?? '0'};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.width1080}) {
    img {
      min-height: 26.2rem;
    }

    &.hide-on-mobile {
      display: none;
    }

    &.show-on-mobile-first {
      display: block;
      border-top-left-radius: 0;
      border-top-right-radius: 2rem;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 2rem;
      margin-bottom: 1rem;
    }
  }
`;

/* TEXT STYLES */

export const TitleText = styled(H4)<{ $color: string }>`
  color: ${({ $color }) => $color};
  font-size: ${({ theme }) => theme.fontSize.size40};
  font-weight: ${({ theme }) => theme.fontWeight.weight900};
  line-height: ${({ theme }) => theme.lineHeight.height115};
  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoint.width768}) {
    font-size: ${({ theme }) => theme.fontSize.size35};
  }
  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoint.width480}) {
    font-size: ${({ theme }) => theme.fontSize.size32};
  }
`;

export const SubTitleText = styled(H4)<{
  $color: string;
  $fontSize?: string;
  $marginBottom?: string;
}>`
  color: ${({ $color }) => $color};
  font-size: ${({ theme, $fontSize }) => $fontSize || theme.fontSize.size24};
  font-weight: ${({ theme }) => theme.fontWeight.weight900};
  line-height: 135%;
  margin-bottom: ${({ $marginBottom }) => $marginBottom};

  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoint.width768}) {
    font-size: ${({ theme }) => theme.fontSize.size22};
  }
  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoint.width480}) {
    font-size: ${({ theme }) => theme.fontSize.size20};
  }
`;

export const RegularText = styled(P)<{
  $color: string;
  $fontSize?: string;
  $isCentered?: boolean;
}>`
  color: ${({ $color }) => $color};
  word-break: break-word;
  font-size: ${({ theme, $fontSize }) => $fontSize || theme.fontSize.size18};
  font-weight: ${({ theme }) => theme.fontWeight.weight400};
  line-height: ${({ theme }) => theme.lineHeight.height150};

  ${({ $isCentered }) => $isCentered && 'text-align: center;'}
  @media only screen and (max-width: ${({ theme }) =>
    theme.breakpoint.width768}) {
    font-size: ${({ theme }) => theme.fontSize.size17};
  }
  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoint.width480}) {
    font-size: ${({ theme }) => theme.fontSize.size16};
  }
`;

export const RegularLinkText = styled.span<{ $color: string }>`
  color: ${({ $color }) => $color};
  font-size: ${({ theme }) => theme.fontSize.size18};
  font-weight: ${({ theme }) => theme.fontWeight.weight400};
  line-height: ${({ theme }) => theme.lineHeight.height150};
  cursor: pointer;
`;
